import React, { useState, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { VideoPlayer } from "./VideoPlayer";
import { fetchWithAuth } from "../../utils/api";
import "../../App.css";

/* ─────── FAQ ITEM ─────── */
function FAQItem({ item, isOpen, onToggle }) {
  return (
    <div className={`hw-faq-item ${isOpen ? "open" : ""}`}>
      <button className="hw-faq-question" onClick={onToggle}>
        <span>{item.question}</span>
        <span className={`hw-faq-chevron ${isOpen ? "rotated" : ""}`}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="hw-faq-answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
          >
            <p style={{ whiteSpace: "pre-wrap" }}>{item.answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─────── HELPER: FORMAT DURATION ─────── */
function formatDuration(duration) {
  if (!duration) return null;
  if (typeof duration === "string" && duration.includes(":")) {
    return duration;
  }
  const totalSeconds = parseInt(duration, 10);
  if (isNaN(totalSeconds)) return duration;
  
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/* ─────── VIDEO CARD ─────── */
function VideoCard({ video, onPlay }) {
  const thumbUrl = video.thumbnailURL || video.thumbnail || video.thumbnailImage;
  const [durationStr, setDurationStr] = useState(null);

  useEffect(() => {
    if (!durationStr && (video.resourceLink || video.url)) {
      const fetchDuration = async () => {
        try {
          const videoUrl = video.resourceLink || video.url;
          if (videoUrl.includes('.m3u8')) {
            const res = await fetch(videoUrl);
            const text = await res.text();
            let totalDuration = 0;
            const regex = /#EXTINF:([\d.]+)/g;
            let match;
            while ((match = regex.exec(text)) !== null) {
              totalDuration += parseFloat(match[1]);
            }
            if (totalDuration > 0) {
              setDurationStr(formatDuration(totalDuration));
            }
          } else {
            const vid = document.createElement('video');
            vid.onloadedmetadata = () => {
              if (vid.duration && Number.isFinite(vid.duration)) {
                setDurationStr(formatDuration(vid.duration));
              }
            };
            vid.src = videoUrl;
          }
        } catch (e) {
          // ignore errors
        }
      };
      fetchDuration();
    }
  }, [video, durationStr]);

  return (
    <motion.div
      className="hw-video-card"
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onPlay(video)}
      layout
    >
      <div className="hw-video-thumb">
        {thumbUrl ? (
          <>
            <img
              src={thumbUrl}
              alt={video.resourceTitle || video.title || "Video thumbnail"}
              className="hw-video-thumb-img"
            />
            <div className="hw-video-play-overlay">
              <i className="fa-solid fa-play"></i>
            </div>
          </>
        ) : (
          <div className="hw-video-thumb-bg">
            <i className="fa-solid fa-play"></i>
          </div>
        )}
        <span className="hw-video-badge">
          {durationStr ? (
            durationStr
          ) : (
            <><i className="fa-solid fa-play-circle"></i> Video</>
          )}
        </span>
      </div>
      <div className="hw-video-info">
        <h4 className="hw-video-title">
          {video.resourceTitle || video.title}
        </h4>
        {(video.resourceDescription || video.description) && (
          <p className="hw-video-desc">
            {(video.resourceDescription || video.description).slice(0, 80)}
            {(video.resourceDescription || video.description).length > 80 ? "…" : ""}
          </p>
        )}
      </div>
    </motion.div>
  );
}

/* ─────── MAIN WIDGET ─────── */
export default function HelpWidget({ role = "admin" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("videos");
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [openFaqIdx, setOpenFaqIdx] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [faqList, setFaqList] = useState([]);
  const [faqLoading, setFaqLoading] = useState(false);
  const [hasFetchedFaq, setHasFetchedFaq] = useState(false);
  const sidebarRef = useRef(null);
  const serverUrl = process.env.REACT_APP_SERVER_URL;

  // Fetch videos on first open
  const fetchVideos = useCallback(async () => {
    if (hasFetched) return;
    try {
      setLoading(true);
      const response = await fetchWithAuth(
        `${serverUrl}/api/resources/get-by-role/${role}`,
        { credentials: "include" }
      );
      if (!response || !response.ok) throw new Error("Failed to fetch");
      const resp = await response.json();
      let data = Array.isArray(resp)
        ? resp
        : Array.isArray(resp?.data)
          ? resp.data
          : [];
      data.sort((a, b) => {
        const aOrder = a.displayOrder ?? -1;
        const bOrder = b.displayOrder ?? -1;
        return aOrder - bOrder;
      });
      setResources(data);
      setHasFetched(true);
    } catch {
      setResources([]);
    } finally {
      setLoading(false);
    }
  }, [serverUrl, role, hasFetched]);

  // Fetch FAQs on first open
  const fetchFAQs = useCallback(async () => {
    if (hasFetchedFaq) return;
    try {
      setFaqLoading(true);
      const response = await fetchWithAuth(
        `${serverUrl}/api/faq/get-all`,
        { credentials: "include" }
      );
      if (!response || !response.ok) throw new Error("Failed to fetch FAQs");
      const resp = await response.json();
      let data = Array.isArray(resp)
        ? resp
        : Array.isArray(resp?.data)
          ? resp.data
          : [];
      data.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
      setFaqList(data);
      setHasFetchedFaq(true);
    } catch {
      setFaqList([]);
    } finally {
      setFaqLoading(false);
    }
  }, [serverUrl, hasFetchedFaq]);

  const handleOpen = () => {
    setIsOpen(true);
    fetchVideos();
    fetchFAQs();
  };

  const handleClose = () => {
    setIsOpen(false);
    setPlayingVideo(null);
  };

  // close on ESC
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") {
        if (playingVideo) setPlayingVideo(null);
        else if (isOpen) handleClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, playingVideo]);

  // click outside sidebar to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target) &&
        !e.target.closest(".hw-fab") &&
        !e.target.closest(".hw-video-modal-overlay")
      ) {
        handleClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  const tabs = [
    { id: "videos", label: "Videos", icon: "fa-solid fa-play-circle" },
    ...(hasFetchedFaq && faqList.length > 0
      ? [{ id: "faq", label: "FAQ", icon: "fa-solid fa-circle-question" }]
      : []),
    { id: "contact", label: "Contact", icon: "fa-solid fa-envelope" },
  ];

  const videoList = Array.isArray(resources) ? resources : [];

  return (
    <>
      {/* ─── FLOATING ACTION BUTTON ─── */}
      <motion.button
        className={`hw-fab ${isOpen ? "hw-fab-active" : ""}`}
        onClick={() => (isOpen ? handleClose() : handleOpen())}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        aria-label="Help"
        title="Need help?"
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.i
              key="close"
              className="fa-solid fa-xmark"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            />
          ) : (
            <motion.i
              key="help"
              className="fa-solid fa-question"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            />
          )}
        </AnimatePresence>
      </motion.button>

      {/* ─── SIDEBAR OVERLAY ─── */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              className="hw-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            />
            <motion.aside
              ref={sidebarRef}
              className="hw-sidebar"
              initial={{ x: "100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "100%", opacity: 0 }}
              transition={{ type: "spring", damping: 28, stiffness: 300 }}
            >
              {/* Sidebar Header */}
              <div className="hw-sidebar-header">
                <div className="hw-sidebar-header-left">
                  <div className="hw-header-icon">
                    <img src="/favicon.ico" alt="Favicon" style={{ width: "24px", height: "24px", objectFit: "contain" }} />
                  </div>
                  <div>
                    <h3>GradeMe AI Help Center</h3>
                    <p>Guides & Support</p>
                  </div>
                </div>
                <button className="hw-close-btn" onClick={handleClose}>
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>

              {/* Tabs */}
              <div className="hw-tabs">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={`hw-tab ${activeTab === tab.id ? "active" : ""}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <i className={tab.icon}></i>
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="hw-content">
                <AnimatePresence mode="wait">
                  {/* ─── VIDEOS TAB ─── */}
                  {activeTab === "videos" && (
                    <motion.div
                      key="videos"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="hw-tab-content"
                    >
                      <div className="hw-tab-intro">
                        <h4>
                          <i className="fa-solid fa-graduation-cap"></i>
                          Video Guides
                        </h4>
                        <p>Watch step-by-step tutorials to get started</p>
                      </div>

                      {loading ? (
                        <div className="hw-loading">
                          <div className="hw-spinner"></div>
                          <span>Loading videos...</span>
                        </div>
                      ) : videoList.length === 0 ? (
                        <div className="hw-empty">
                          <i className="fa-solid fa-film"></i>
                          <p>No video guides available yet.</p>
                        </div>
                      ) : (
                        <div className="hw-video-list">
                          {videoList.map((video, idx) => (
                            <VideoCard
                              key={video._id || video.id || idx}
                              video={video}
                              onPlay={setPlayingVideo}
                            />
                          ))}
                        </div>
                      )}
                    </motion.div>
                  )}

                  {/* ─── FAQ TAB ─── */}
                  {activeTab === "faq" && (
                    <motion.div
                      key="faq"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="hw-tab-content"
                    >
                      <div className="hw-tab-intro">
                        <h4>
                          <i className="fa-solid fa-circle-question"></i>
                          Frequently Asked Questions
                        </h4>
                        <p>Quick answers to common questions</p>
                      </div>

                      {faqLoading ? (
                        <div className="hw-loading">
                          <div className="hw-spinner"></div>
                          <span>Loading FAQs...</span>
                        </div>
                      ) : faqList.length === 0 ? (
                        <div className="hw-empty">
                          <i className="fa-solid fa-circle-question"></i>
                          <p>No FAQs available yet.</p>
                        </div>
                      ) : (
                        <div className="hw-faq-list">
                          {faqList.map((item, idx) => (
                            <FAQItem
                              key={item._id || idx}
                              item={item}
                              isOpen={openFaqIdx === idx}
                              onToggle={() =>
                                setOpenFaqIdx(openFaqIdx === idx ? null : idx)
                              }
                            />
                          ))}
                        </div>
                      )}
                    </motion.div>
                  )}

                  {/* ─── CONTACT TAB ─── */}
                  {activeTab === "contact" && (
                    <motion.div
                      key="contact"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="hw-tab-content"
                    >
                      <div className="hw-tab-intro">
                        <h4>
                          <i className="fa-solid fa-headset"></i>
                          Get in Touch
                        </h4>
                        <p>We're here to help you</p>
                      </div>

                      <div className="hw-contact-card">
                        <div className="hw-contact-icon-wrap">
                          <i className="fa-solid fa-envelope"></i>
                        </div>
                        <div className="hw-contact-info">
                          <span className="hw-contact-label">Email Support</span>
                          <a
                            href="mailto:support@grademe-ai.com"
                            className="hw-contact-value"
                          >
                            support@grademe-ai.com
                          </a>
                        </div>
                      </div>

                      <div className="hw-contact-card">
                        <div className="hw-contact-icon-wrap hw-contact-icon-whatsapp">
                          <i className="fa-brands fa-whatsapp"></i>
                        </div>
                        <div className="hw-contact-info">
                          <span className="hw-contact-label">WhatsApp</span>
                          <a
                            href="https://wa.me/919381369640"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hw-contact-value"
                          >
                            Drop A Message on Our WhatsApp
                          </a>
                        </div>
                      </div>

                      <div className="hw-contact-card">
                        <div className="hw-contact-icon-wrap hw-contact-icon-clock">
                          <i className="fa-solid fa-clock"></i>
                        </div>
                        <div className="hw-contact-info">
                          <span className="hw-contact-label">Response Time</span>
                          <span className="hw-contact-value hw-contact-text">
                            Within 24 hours on business days
                          </span>
                        </div>
                      </div>
                     {(hasFetchedFaq && faqList.length > 0) &&
                      <div className="hw-contact-note">
                        <i className="fa-solid fa-lightbulb"></i>
                        <p>
                          Check our <button className="hw-link-btn" onClick={() => setActiveTab("faq")}>FAQ section</button> for
                          instant answers to common questions before reaching out.
                        </p>
                      </div>
                      }
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Footer */}
              <div className="hw-sidebar-footer">
                <span>
                  <i className="fa-solid fa-shield-halved"></i>
                  GradeMe AI Help Center
                </span>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ─── VIDEO PLAYER MODAL ─── */}
      <AnimatePresence>
        {playingVideo && (
          <motion.div
            className="hw-video-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setPlayingVideo(null)}
          >
            <motion.div
              className="hw-video-modal"
              initial={{ scale: 0.85, opacity: 0, y: 40 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.85, opacity: 0, y: 40 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="hw-video-modal-header">
                <h3>
                  {playingVideo.resourceTitle || playingVideo.title}
                </h3>
                <button
                  className="hw-video-modal-close"
                  onClick={() => setPlayingVideo(null)}
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>
              <div className="hw-video-modal-body">
                <VideoPlayer
                  src={playingVideo.resourceLink || playingVideo.url}
                  type={playingVideo.type || "application/x-mpegURL"}
                  width="100%"
                  height="100%"
                  autoPlay={true}
                />
              </div>
              {(playingVideo.resourceDescription || playingVideo.description) && (
                <div className="hw-video-modal-desc">
                  <p>{playingVideo.resourceDescription || playingVideo.description}</p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
